import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import {
  AlertCircleIcon,
  ChevronDownIcon,
  CircleCheckIcon,
  LoaderIcon,
  LocationIcon,
} from '@hero/web-icons';
import type { Language, PartyRole } from '@hero/schema';
import type { OrderLocationChangePreview } from '@hero/api';
import { computeAmountPreview, formatPickupWindow, localizeCityLabel } from '@hero/core';
import {
  containsGoogleMapsUrl,
  formatLoadSummary,
  stagedPlaceLabels,
  type StagedLoad,
} from '@hero/agent';
import { Button } from '../../../ui/Button/Button';
import { ChoiceCard } from '../../../app/ChoiceCard';
import { ChooseLocationModal, type ChosenLocation } from '../../../app/Map';
import { Price } from '../../../app/Price/Price';
import { PriceCategoryBadge } from '../../../app/PriceCategoryBadge';
import { PriceInput } from '../../../app/PriceInput';
import { RouteArrowDisplay } from '../../../app/RouteArrowDisplay/RouteArrowDisplay';
import { ClientRolesBadge } from '../../Client/Status/ClientRolesBadge';
import { useAgentChatManager } from './useAgentChatManager';
import { useAgentChatStore } from './store';
import type { PendingConfirmation } from './types';
import { CreateOrderPayloadPreview } from './CreateOrderPayloadPreview';
import { SavedLocationPayloadPreview } from './SavedLocationPayloadPreview';
import { getConfirmationBlockers } from './getConfirmationBlockers';
import { useCardBusyReasons, useReportCardBusy } from './useCardBusy';
import { EntityLink } from './widgets/EntityLink';
import type { HeroEntityKind } from './widgets/openEntityDrawer';
import { normalizeEntityId } from './widgets/normalizeEntityId';
import {
  CardField as Field,
  CardNumberField,
  CardSwitchField,
  CardTextField,
  ClientRefField,
  EntityRefField,
  humaniseKey as humanise,
} from './CardFields';

/**
 * Confirmation card — the user reviews the staged action and clicks
 * "Confirm" / "Decline".
 *
 * The card has TWO display modes:
 *
 * 1. **Expanded** (default for a solo card) — full editor visible
 *    under the compact summary row.
 * 2. **Collapsed** (default when ≥2 cards stack in one turn) — only a
 *    compact one-line summary + per-card Confirm / Cancel. The user
 *    taps the row to expand for edits. This keeps a 30-client bulk
 *    confirmation manageable: 30 compact rows fit on screen, while
 *    each card retains its own scoped `OrderDraftProvider` so
 *    cross-card edits remain isolated.
 *
 * For `create_order` the summary row lives INSIDE the
 * `CreateOrderPayloadPreview` component because it reads from the
 * same scoped store as the editor. For all other actions, the card
 * synthesizes a tiny summary from the staged payload itself.
 */
export const ConfirmationCard = ({
  pending,
  stackCollapsed = false,
}: {
  pending: PendingConfirmation;
  /** When true the card starts collapsed (multi-card stack). */
  stackCollapsed?: boolean;
}) => {
  const { t } = useTranslation('m-agent');
  const manager = useAgentChatManager();
  const confirmationBatch = useAgentChatStore(s => s.confirmationBatch);
  const [expanded, setExpanded] = useState(!stackCollapsed);

  const isBatchLocked = !!confirmationBatch;
  const batchResult = confirmationBatch?.results.find(
    r => r.confirmationId === pending.id,
  );
  const isDone = batchResult?.ok === true;
  const isFailed = batchResult?.ok === false;
  const isProcessing =
    (isBatchLocked &&
      confirmationBatch?.mode === 'confirm' &&
      !batchResult) ||
    manager.resolvingConfirmationId === pending.id;
  const interactionsDisabled = manager.isConfirming;
  const blockers = getConfirmationBlockers(pending.action, pending.payload);
  const blockerLabels = blockers.map(b =>
    t(`confirmation.blockers.${b}`, { defaultValue: humanise(b) }),
  );
  // Editing a stop re-prices the order; the card is unconfirmable until
  // that settles, or the payload posted is the one from before the edit.
  const busyReasons = useCardBusyReasons(pending.id);
  const busyReason = busyReasons[0];

  useEffect(() => {
    if (isBatchLocked) {
      setExpanded(false);
    }
  }, [isBatchLocked]);

  const toggleExpanded = () => {
    if (interactionsDisabled) return;
    setExpanded(v => !v);
  };

  return (
    <motion.div
      initial={isBatchLocked ? false : { opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 240, damping: 22 }}
      className={cn(
        'w-full overflow-hidden rounded-2xl border bg-white shadow-sm ring-1 ring-slate-100',
        isDone && 'border-emerald-200',
        isFailed && 'border-red-200',
        !isDone && !isFailed && 'border-slate-200',
      )}
    >
      <CardSummary
        action={pending.action}
        payload={pending.payload}
        confirmationId={pending.id}
        expanded={expanded}
        onToggleExpanded={toggleExpanded}
        interactionsDisabled={interactionsDisabled}
        isProcessing={isProcessing}
        isDone={isDone}
      />
      <div
        className={cn(
          'flex items-center justify-end gap-2 border-slate-100 px-4 py-2',
          expanded && 'border-t',
        )}
      >
        {isProcessing ? (
          <span className='me-auto flex items-center gap-1.5 text-xs text-primary-700'>
            <LoaderIcon className='size-3.5 animate-spin' />
            {t('confirmation.batch.processing', 'Creating…')}
          </span>
        ) : busyReason ? (
          <span className='me-auto flex items-center gap-1.5 text-xs text-slate-500'>
            <LoaderIcon className='size-3.5 animate-spin' />
            {t(`confirmation.busy.${busyReason}`, {
              defaultValue:
                busyReason === 'price' ? 'Updating price…' : 'Updating location…',
            })}
          </span>
        ) : blockerLabels.length > 0 ? (
          // Name what's missing next to the dead button, so the card
          // never looks broken.
          <span className='me-auto text-[11px] text-amber-700'>
            {t('confirmation.blockers.hint', {
              defaultValue: 'Still needed: {{fields}}',
              fields: blockerLabels.join(', '),
            })}
          </span>
        ) : null}
        <Button
          variant='ghost'
          size='sm'
          onClick={() => manager.respondToConfirmation('decline', pending.id)}
          disabled={interactionsDisabled}
        >
          {t('confirmation.decline', 'Cancel')}
        </Button>
        <Button
          size='sm'
          onClick={() => manager.respondToConfirmation('confirm', pending.id)}
          disabled={interactionsDisabled || !!busyReason || blockerLabels.length > 0}
          className='gap-1'
        >
          <CircleCheckIcon className='size-4' />
          {t('confirmation.confirm', 'Confirm')}
        </Button>
      </div>
    </motion.div>
  );
};

/**
 * Renders the summary row + (when expanded) the action-specific
 * payload body. For `create_order` both are owned by
 * `CreateOrderPayloadPreview` because they share a scoped store; for
 * other actions, we synthesize a one-line summary from the payload
 * here.
 */
const CardSummary = ({
  confirmationId,
  action,
  payload,
  expanded,
  onToggleExpanded,
  interactionsDisabled = false,
  isProcessing = false,
  isDone = false,
}: {
  confirmationId: string;
  action: string;
  payload: Record<string, unknown>;
  expanded: boolean;
  onToggleExpanded: () => void;
  interactionsDisabled?: boolean;
  isProcessing?: boolean;
  isDone?: boolean;
}) => {
  if (action === 'create_order') {
    return (
      <CreateOrderPayloadPreview
        confirmationId={confirmationId}
        payload={payload}
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
        interactionsDisabled={interactionsDisabled}
        isProcessing={isProcessing}
        isDone={isDone}
      />
    );
  }

  return (
    <>
      <GenericSummaryRow
        action={action}
        payload={payload}
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
        interactionsDisabled={interactionsDisabled}
        isProcessing={isProcessing}
        isDone={isDone}
      />
      {expanded ? (
        <div className='space-y-2 px-4 pb-2.5 pt-1'>
          {action === 'create_client' ? (
            <CreateClientPayloadPreview
              confirmationId={confirmationId}
              payload={payload}
            />
          ) : action === 'create_saved_location' || action === 'update_saved_location' ? (
            <SavedLocationPayloadPreview
              confirmationId={confirmationId}
              payload={payload}
            />
          ) : action === 'assign_driver_to_order' &&
            hasAssignDriverDisplay(payload) ? (
            <AssignDriverPayloadPreview payload={payload} />
          ) : action === 'edit_order' ? (
            <EditOrderPayloadPreview
              confirmationId={confirmationId}
              payload={payload}
              interactionsDisabled={interactionsDisabled || isProcessing || isDone}
            />
          ) : action === 'bulk_edit_orders' ? (
            <BulkEditOrdersPayloadPreview payload={payload} />
          ) : (
            <GenericPayloadPreview
              confirmationId={confirmationId}
              payload={payload}
            />
          )}
        </div>
      ) : null}
    </>
  );
};

/**
 * One-line summary for non-`create_order` actions (e.g. create_client,
 * assign_driver). Mirrors `OrderDraftSummaryRow` styling so a stacked
 * mix of action types reads as a consistent list.
 */
const GenericSummaryRow = ({
  action,
  payload,
  expanded,
  onToggleExpanded,
  interactionsDisabled = false,
  isProcessing = false,
  isDone = false,
}: {
  action: string;
  payload: Record<string, unknown>;
  expanded: boolean;
  onToggleExpanded: () => void;
  interactionsDisabled?: boolean;
  isProcessing?: boolean;
  isDone?: boolean;
}) => {
  const { t } = useTranslation('m-agent');
  const headline = summariseAction(action, payload);
  return (
    <button
      type='button'
      onClick={() => {
        if (interactionsDisabled) return;
        onToggleExpanded();
      }}
      disabled={interactionsDisabled}
      aria-expanded={expanded}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-start text-xs transition-colors',
        interactionsDisabled ? 'cursor-default' : 'hover:bg-slate-50',
        isDone && 'bg-emerald-50/40',
      )}
    >
      {isProcessing ? (
        <LoaderIcon className='size-4 shrink-0 animate-spin text-primary-600' />
      ) : isDone ? (
        <CircleCheckIcon className='size-4 shrink-0 text-emerald-600' />
      ) : null}
      <span className='flex min-w-0 flex-1 items-center gap-2'>
        {/* Sentence case, not SHOUTED — the badge names the action,
            it isn't a warning label. */}
        <span className='shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700'>
          {t(`confirmation.action_badge.${action}`, { defaultValue: humanise(action) })}
        </span>
        <span className='truncate text-sm text-slate-800'>{headline}</span>
      </span>
      {!interactionsDisabled ? (
        <span className='ms-auto flex shrink-0 items-center gap-1 text-[11px] font-medium text-slate-500'>
          <span>
            {expanded
              ? t('confirmation.order.collapse', 'Hide')
              : t('confirmation.order.expand', 'Edit')}
          </span>
          <ChevronDownIcon
            className={cn('size-3.5 transition-transform', expanded && 'rotate-180')}
          />
        </span>
      ) : null}
    </button>
  );
};

/**
 * Best-effort one-liner for an arbitrary staged payload. Used by
 * `GenericSummaryRow` so a 30-card mix of create_client + assign
 * actions still gets a scannable summary line — falls back to the
 * humanised action name when no obvious title field is present.
 */
const summariseAction = (action: string, payload: Record<string, unknown>): string => {
  const pick = (key: string): string | undefined => {
    const v = payload[key];
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
  };
  if (action === 'create_client') {
    return pick('clientName') ?? pick('name') ?? humanise(action);
  }
  if (action === 'assign_driver_to_order') {
    const driverName = pick('driverName');
    const orderRef = pick('orderRef');
    if (driverName || orderRef) {
      return [driverName, orderRef].filter(Boolean).join(' — ');
    }
    return humanise(action);
  }
  if (action === 'edit_order') {
    return pick('orderRef') ?? humanise(action);
  }
  if (action === 'create_saved_location' || action === 'update_saved_location') {
    return pick('name') ?? pick('locationLabel') ?? humanise(action);
  }
  // `summary` is built server-side in the conversation language by
  // `summarizeBulkMutation`, so it needs no translation here.
  if (action === 'bulk_edit_orders') {
    return pick('summary') ?? humanise(action);
  }
  return pick('clientName') ?? pick('name') ?? pick('title') ?? humanise(action);
};

const readString = (payload: Record<string, unknown>, key: string): string | undefined => {
  const v = payload[key];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
};

/** Old cards (staged before the display-snapshot rollout) only carry ids. */
const hasAssignDriverDisplay = (payload: Record<string, unknown>): boolean =>
  !!(
    readString(payload, 'driverName') ??
    readString(payload, 'orderRef') ??
    readString(payload, 'reason')
  );

/**
 * Assign-driver card body — driver name (tappable, opens the driver
 * drawer), order ref + pickup → dropoff route (arrow auto-flips in
 * RTL), and the "why this driver" reason. Raw ObjectIds never render.
 */
const AssignDriverPayloadPreview = ({
  payload,
}: {
  payload: Record<string, unknown>;
}) => {
  const { t } = useTranslation('m-agent');
  const driverId = readString(payload, 'driverId');
  const driverName = readString(payload, 'driverName');
  const orderId = readString(payload, 'orderId');
  const orderRef = readString(payload, 'orderRef');
  const pickupCity = readString(payload, 'pickupCity');
  const dropoffCity = readString(payload, 'dropoffCity');
  const reason = readString(payload, 'reason');

  return (
    <div className='space-y-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs'>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        <div className='flex flex-col gap-1'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.assign.driver', 'Driver')}
          </span>
          <span>
            {driverId ? (
              <EntityLink
                kind='driver'
                entityId={driverId}
                label={driverName ?? t('confirmation.assign.driver', 'Driver')}
                variant='inline'
              />
            ) : (
              <span className='text-sm text-slate-800'>{driverName}</span>
            )}
          </span>
        </div>
        <div className='flex flex-col gap-1'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.assign.order', 'Order')}
          </span>
          <span className='flex flex-wrap items-center gap-1.5'>
            {orderId && orderRef ? (
              <EntityLink
                kind='order'
                entityId={orderId}
                label={orderRef}
                variant='inline'
              />
            ) : (
              <span className='text-sm text-slate-800'>{orderRef}</span>
            )}
            {pickupCity || dropoffCity ? (
              <RouteArrowDisplay
                origin={{ displayName: pickupCity ?? '' }}
                destinations={[{ displayName: dropoffCity ?? '' }]}
                size='small'
                className='text-slate-600'
              />
            ) : null}
          </span>
        </div>
      </div>
      {reason ? (
        <div className='flex flex-col gap-0.5 border-t border-slate-100 pt-2'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.assign.reason', 'Why this driver')}
          </span>
          <span className='text-sm leading-5 text-slate-800'>{reason}</span>
        </div>
      ) : null}
    </div>
  );
};

/**
 * New-client card body. The staged name, contact, phone, and email
 * are all inputs: the agent transcribed them from a chat message, so
 * a typo in a phone number is likelier here than anywhere else, and
 * fixing it should not cost the user another round-trip.
 */
const CreateClientPayloadPreview = ({
  confirmationId,
  payload,
}: {
  confirmationId: string;
  payload: Record<string, unknown>;
}) => {
  const { t } = useTranslation(['m-agent', 'm-client']);
  const updatePayload = useAgentChatStore(s => s.updatePendingConfirmationPayload);
  // Older cards staged the name under `name`; write back to whichever
  // key the payload actually carries so the confirm POST stays valid.
  const nameKey = payload.clientName != null || payload.name == null ? 'clientName' : 'name';
  // Never trim a value that feeds a controlled input: the trimmed string
  // comes straight back as `value`, so a space typed at the end vanishes on
  // the next render and the space key looks broken. The confirm POST trims
  // server-side anyway.
  const clientName = String(payload[nameKey] ?? '');
  const personName = String(payload.personName ?? '');
  const personPhone = String(payload.personPhone ?? '');
  const personEmail = String(payload.personEmail ?? '');
  const roles = parseRoles(payload.roles);

  return (
    <div className='grid grid-cols-1 gap-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs sm:grid-cols-2'>
      <CardTextField
        label={t('confirmation.fields.client_name', 'Client name')}
        value={clientName}
        onChange={next => updatePayload(confirmationId, { [nameKey]: next })}
      />
      <CardTextField
        label={t('confirmation.fields.person_name', 'Contact name')}
        value={personName}
        onChange={next => updatePayload(confirmationId, { personName: next })}
      />
      <CardTextField
        label={t('confirmation.fields.person_phone', 'Phone')}
        value={personPhone}
        dir='ltr'
        onChange={next => updatePayload(confirmationId, { personPhone: next })}
      />
      <CardTextField
        label={t('confirmation.fields.person_email', 'Email')}
        value={personEmail}
        dir='ltr'
        onChange={next => updatePayload(confirmationId, { personEmail: next })}
      />
      {roles.length > 0 ? (
        <RolesField label={t('confirmation.fields.roles', 'Roles')} roles={roles} />
      ) : null}
    </div>
  );
};

/**
 * Single-order edit card body.
 *
 * A location edit reads as two blocks — the route it becomes, then what
 * that does to the price — deliberately the same shape and wording as
 * {@link LocationChangePriceModal}, which is where a user doing the same
 * edit by hand sees it. Two surfaces for one decision may not describe it
 * two different ways.
 *
 * Everything else is the unchanged-fields-omitted list of what the edit
 * touches, and only appears when the edit is not about the route: a
 * "New price" row above a price block that already names the number is
 * the same fact twice.
 */
const EditOrderPayloadPreview = ({
  confirmationId,
  payload,
  interactionsDisabled = false,
}: {
  confirmationId: string;
  payload: Record<string, unknown>;
  interactionsDisabled?: boolean;
}) => {
  const { t, i18n } = useTranslation('m-agent');
  const updatePayload = useAgentChatStore(s => s.updatePendingConfirmationPayload);
  const [mapStop, setMapStop] = useState<'pickup' | 'dropoff' | null>(null);
  // A pin the user is still moving is not a pin they have agreed to.
  useReportCardBusy(confirmationId, 'location', mapStop != null);
  const orderId = readString(payload, 'orderId');
  const orderRef = readString(payload, 'orderRef');
  const setPrice = readNumber(payload, 'setPrice');
  const instructions = readString(payload, 'instructions');
  const statusAction = readString(payload, 'statusAction');
  const clientName = readString(payload, 'clientName');
  const pickupQuery = readString(payload, 'pickupQuery');
  const dropoffQuery = readString(payload, 'dropoffQuery');
  const pickupCity = readString(payload, 'pickupCity');
  const dropoffCity = readString(payload, 'dropoffCity');
  const orderClient = readParty(payload.orderClient);
  const orderDriver = readParty(payload.orderDriver);
  const stagedPickupLabel = parseStopLabel(payload.pickupLabel);
  const stagedDropoffLabel = parseStopLabel(payload.dropoffLabel);
  const fees = parseEditFees(payload.addFees);
  const preview = parseLocationChangePreview(payload.locationChangePreview);
  const policy = readString(payload, 'locationChangePricing');
  const pickupChange = preview?.changes.find(c => c.type === 'pickup');
  const dropoffChange = preview?.changes.find(c => c.type === 'dropoff');
  // The staged label carries the place name; the preview only knows the
  // city. Either beats echoing the Maps URL back at the user.
  const pickupLabel = stagedPickupLabel ?? asStopLabel(pickupChange?.newCity);
  const dropoffLabel = stagedDropoffLabel ?? asStopLabel(dropoffChange?.newCity);

  // Both halves of the route come from different sources — the order's
  // stored city and a fresh geocode — so they arrive in whichever language
  // each was written in. Localising both is what stops the card reading
  // "Riyadh → عرعر".
  const lang = (i18n.language?.split('-')[0] ?? 'en') as Language;
  const city = (value?: string): string | undefined =>
    value ? localizeCityLabel(value, lang) : undefined;

  const load = formatLoadSummary(payload as StagedLoad, lang);
  const pickupWindow = formatPickupWindow(parseStagedDateRange(payload.pickupDate)) || undefined;

  const isLocationEdit = Boolean(pickupQuery || dropoffQuery);
  const from = {
    old: city(pickupChange?.previousCity ?? pickupCity),
    new: city(pickupLabel?.city) ?? city(pickupCity),
  };
  const to = {
    old: city(dropoffChange?.previousCity ?? dropoffCity),
    new: city(dropoffLabel?.city) ?? city(dropoffCity),
  };
  const routeMoved = from.old !== from.new || to.old !== to.new;

  // Which price confirming actually lands on. `keep_previous` is the
  // resolver's answer when the new lane has no rate, so the two numbers
  // are often identical — striking one through then would invent a
  // change the user is not making.
  const appliedPrice =
    setPrice ??
    (preview
      ? policy === 'keep_previous'
        ? preview.previousPrice
        : (preview.resolvedPrice ?? preview.previousPrice)
      : undefined);
  const priceMoved = preview != null && appliedPrice !== preview.previousPrice;

  const routePrice = preview?.hasRateOnNewRoute ? preview.resolvedPrice : undefined;
  // The narrowed read covers the card's text; the fee / tax / currency
  // half of the preview is what turns a load price into the total the
  // backend will write.
  const fullPreview = readLocationChangePreview(payload.locationChangePreview);

  /**
   * The cross-city price decision, inline — the same two options, defaults,
   * and wording as {@link LocationChangePriceModal}, which is the surface a
   * user gets for this decision outside the chat.
   *
   * Local state mirrors the payload rather than deriving from it: the
   * custom field has to survive being emptied mid-typing, and a value
   * derived from `setPrice` would snap back to the seed on every keystroke
   * that clears it.
   */
  const [priceMode, setPriceMode] = useState<'route' | 'custom'>('route');
  const [customPrice, setCustomPrice] = useState<number | undefined>(undefined);

  // Re-seed whenever the staged candidate changes — a new lane is a new
  // question, and the answer to the previous one does not carry over.
  const priceSeedKey = preview?.crossCity
    ? `${confirmationId}|${preview.newRouteLabel}|${routePrice ?? ''}|${preview.previousPrice}`
    : '';
  useEffect(() => {
    if (!priceSeedKey || !preview) return;
    const staged = readNumber(payload, 'setPrice');
    const mode = staged != null ? 'custom' : routePrice != null ? 'route' : 'custom';
    const seeded = staged ?? preview.previousPrice;
    setPriceMode(mode);
    setCustomPrice(seeded);
    // The payload must say what the card shows, or Confirm posts the
    // resolver's recommendation while the user reads their own number.
    updatePayload(
      confirmationId,
      mode === 'route'
        ? { locationChangePricing: 'resolve_route', setPrice: undefined }
        : { locationChangePricing: undefined, setPrice: seeded },
    );
    // Seeding is keyed on the candidate, not on every payload change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceSeedKey]);

  const chooseRoutePrice = () => {
    setPriceMode('route');
    updatePayload(confirmationId, {
      locationChangePricing: 'resolve_route',
      setPrice: undefined,
    });
  };

  // A named price is staged as the price and nothing else: a policy left
  // beside it makes the applied number depend on which the backend reads
  // first.
  const chooseCustomPrice = (next?: number) => {
    setPriceMode('custom');
    setCustomPrice(next);
    updatePayload(confirmationId, { locationChangePricing: undefined, setPrice: next });
  };

  // The one number nobody can derive by eye: what a load price becomes
  // once this leg's fees and tax land on it. Through
  // `computeAmountPreview` so it matches what the backend will write, and
  // per option so the two prices are compared on the same terms.
  const totalFor = (price?: number): number | undefined => {
    if (price == null || !fullPreview) return undefined;
    const total = computeAmountPreview({
      itemsOriginalSubtotal: price,
      fees: { applied: fullPreview.fees },
      taxId: fullPreview.taxId,
      currency: fullPreview.currency,
    }).total;
    // An untaxed, fee-free leg would just repeat the load price back.
    return total === price ? undefined : total;
  };

  // Which pin each moved stop currently carries, so the map opens where
  // the confirm would actually send the truck.
  const stagedDestinations = readDestinations(payload.destinations);
  const stopPin = (role: 'pickup' | 'dropoff') =>
    stagedDestinations.find(d => d.type === role);

  /**
   * Adjust a staged stop from the card.
   *
   * The pin, its labels, and its provenance all travel together: a moved
   * marker that keeps the old name and the old Maps link describes a
   * place that no longer exists on this order.
   */
  const handleStopChosen = (role: 'pickup' | 'dropoff', chosen: ChosenLocation) => {
    const next = stagedDestinations.map(dest =>
      dest.type === role
        ? {
            ...dest,
            location: { lat: chosen.lat, lng: chosen.lng },
            isAccurateLocation: chosen.isAccurate ?? true,
            locationUrl: chosen.locationUrl,
            savedLocationId: chosen.savedLocationId,
          }
        : dest,
    );
    updatePayload(confirmationId, {
      destinations: next,
      [role === 'pickup' ? 'pickupLabel' : 'dropoffLabel']: {
        name: chosen.name,
        city: chosen.city,
      },
    });
    setMapStop(null);
  };

  return (
    <div className='space-y-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs'>
      <dl className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        {orderRef ? (
          <div className='flex flex-col gap-0.5 sm:col-span-2'>
            <dt className='font-medium text-slate-500'>
              {t('confirmation.assign.order', 'Order')}
            </dt>
            <dd className='flex flex-wrap items-center gap-1.5 text-sm text-slate-800'>
              {orderId ? (
                <EntityLink
                  kind='order'
                  entityId={orderId}
                  label={orderRef}
                  variant='inline'
                />
              ) : (
                orderRef
              )}
              {/* Whose order this is, and who is already driving it. A
                  ref number identifies the order to the system; these
                  identify it to the user about to change it. */}
              {orderClient ? <PartyChip kind='client' party={orderClient} /> : null}
              {orderDriver ? <PartyChip kind='driver' party={orderDriver} /> : null}
              {/* The route the edit applies to — but only when it is
                  context. When the edit *is* the route, the block below
                  says it properly and repeating it here reads as two
                  different changes. */}
              {!isLocationEdit && (from.old || to.old) ? (
                <RouteArrowDisplay
                  origin={{ displayName: from.old ?? '' }}
                  destinations={[{ displayName: to.old ?? '' }]}
                  size='small'
                  className='text-slate-600'
                />
              ) : null}
            </dd>
          </div>
        ) : null}
        {setPrice != null && !isLocationEdit ? (
          <div className='flex flex-col gap-0.5'>
            <dt className='font-medium text-slate-500'>
              {t('confirmation.edit.new_price', 'New price')}
            </dt>
            <dd className='text-sm text-slate-800'>
              <Price amount={setPrice} className='font-semibold' />
            </dd>
          </div>
        ) : null}
        {clientName ? (
          <Field label={t('confirmation.fields.client_name', 'Client')} value={clientName} />
        ) : null}
        {/* Cargo, vehicle, temperature — the same phrasing the WhatsApp
            confirmation uses, so one staged change reads identically on
            both channels. */}
        {load ? <Field label={t('confirmation.edit.load', 'Load')} value={load} /> : null}
        {pickupWindow ? (
          <Field label={t('confirmation.edit.pickup_time', 'Pickup time')} value={pickupWindow} />
        ) : null}
        {instructions ? (
          <Field
            label={t('confirmation.edit.instructions', 'Instructions')}
            value={instructions}
          />
        ) : null}
      </dl>

      {/* A lifecycle change is not a field on the order — it ends or
          advances it — so it gets its own line the eye lands on. */}
      {statusAction ? (
        <p className='flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1.5 text-sm font-medium text-amber-900'>
          <AlertCircleIcon className='size-4 shrink-0' />
          {t(`confirmation.edit.status_${statusAction}`, statusAction)}
        </p>
      ) : null}

      {fees.length > 0 ? (
        <div className='flex flex-col gap-1 border-t border-slate-100 pt-2'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.edit.added_fees', 'Added fees')}
          </span>
          <ul className='space-y-0.5'>
            {fees.map((fee, i) => (
              <li key={i} className='flex items-center gap-2 text-sm text-slate-800'>
                <span className='truncate'>{fee.label}</span>
                <Price amount={fee.amount} className='ms-auto shrink-0 font-medium' />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {isLocationEdit ? (
        <div className='flex flex-col gap-1.5 rounded-xl border border-slate-100 bg-white px-3 py-2'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.edit.route', 'Route')}
          </span>
          <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
            {routeMoved && (from.old || to.old) ? (
              <RouteArrowDisplay
                origin={{ displayName: from.old ?? '' }}
                destinations={[{ displayName: to.old ?? '' }]}
                size='small'
                className='text-slate-400 line-through'
              />
            ) : null}
            <RouteArrowDisplay
              origin={{ displayName: from.new ?? '' }}
              destinations={[{ displayName: to.new ?? '' }]}
              size='small'
              className='font-medium text-slate-900'
            />
          </div>
          {/* The stop itself: the city it lands in, then the place
              inside it — the part the pin carries and the route line
              cannot say. */}
          {pickupQuery ? (
            <StopPlaceLine
              label={t('confirmation.edit.new_pickup', 'New pickup')}
              city={from.new}
              place={stagedPlaceLabels(pickupLabel).place}
              fallback={pickupLabel ? undefined : pickupQuery}
              onOpenMap={
                !interactionsDisabled && stopPin('pickup') ? () => setMapStop('pickup') : undefined
              }
            />
          ) : null}
          {dropoffQuery ? (
            <StopPlaceLine
              label={t('confirmation.edit.new_dropoff', 'New dropoff')}
              city={to.new}
              place={stagedPlaceLabels(dropoffLabel).place}
              fallback={dropoffLabel ? undefined : dropoffQuery}
              onOpenMap={
                !interactionsDisabled && stopPin('dropoff') ? () => setMapStop('dropoff') : undefined
              }
            />
          ) : null}
          {preview?.crossCity && preview.distanceKm != null ? (
            <span className='text-xs text-slate-500'>
              {t('confirmation.edit.distance_km', '{{km}} km from the original stop', {
                km: Math.round(preview.distanceKm),
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* A same-city move does not touch the price, so it is a fact, not
          a decision — stating it is enough. */}
      {preview && !preview.crossCity ? (
        <div className='flex flex-col gap-1 rounded-xl border border-slate-100 bg-white px-3 py-2'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.edit.price', 'Price')}
          </span>
          <span className='flex flex-wrap items-center gap-2'>
            <Price
              amount={appliedPrice ?? preview.previousPrice}
              className='text-sm font-semibold'
            />
            <PriceCategoryBadge
              category={setPrice != null ? 'one_time' : fullPreview?.previousPriceCategory}
            />
          </span>
          <span className='text-xs text-slate-500'>
            {t('confirmation.edit.price_unchanged', 'Same city — the price does not change.')}
          </span>
        </div>
      ) : null}

      {/* Crossing a city line reprices the order, so the decision belongs
          on the card rather than behind a note: the rate on file is
          pre-selected when there is one, and the custom field opens filled
          with the current price so "leave it as it was" is already typed. */}
      {preview?.crossCity ? (
        <div className='flex flex-col gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.edit.price', 'Price')}
          </span>

          {/* Kept on screen even with nothing on file: an option that
              silently disappears reads as an option the card never had,
              and "why didn't it use the route price?" is exactly the
              question this answers. */}
          <ChoiceCard
            selected={priceMode === 'route'}
            disabled={interactionsDisabled || routePrice == null}
            onSelect={chooseRoutePrice}
            label={
              <span className='inline-flex flex-wrap items-center gap-1.5'>
                {t('confirmation.edit.price_route_option', 'Rate for the new route')}
                {routePrice != null ? (
                  <PriceCategoryBadge category={fullPreview?.resolvedCategory} />
                ) : null}
              </span>
            }
            hint={
              routePrice == null
                ? t(
                    'confirmation.edit.price_no_rate',
                    'No agreed price with this client and no public rate on this route.',
                  )
                : undefined
            }>
            {routePrice != null ? (
              <div className='flex flex-col gap-0.5'>
                <Price
                  amount={routePrice}
                  currency={fullPreview?.currency}
                  className='text-sm font-semibold'
                />
                <TotalWithTax amount={totalFor(routePrice)} currency={fullPreview?.currency} />
              </div>
            ) : null}
          </ChoiceCard>

          <ChoiceCard
            selected={priceMode === 'custom'}
            disabled={interactionsDisabled}
            onSelect={() => chooseCustomPrice(customPrice ?? preview.previousPrice)}
            label={t('confirmation.edit.price_custom_option', 'Custom price')}>
            {priceMode === 'custom' ? (
              <div className='flex flex-col gap-1'>
                <PriceInput
                  value={customPrice}
                  onChange={chooseCustomPrice}
                  currency={fullPreview?.currency}
                  disabled={interactionsDisabled}
                />
                <TotalWithTax amount={totalFor(customPrice)} currency={fullPreview?.currency} />
              </div>
            ) : (
              <Price
                amount={preview.previousPrice}
                currency={fullPreview?.currency}
                className='text-sm font-semibold text-slate-500'
              />
            )}
          </ChoiceCard>
        </div>
      ) : null}

      {/* Same picker the saved-place card uses: a stop the user can only
          read about is a stop they have to take on trust. */}
      {mapStop ? (
        <ChooseLocationModal
          open
          onOpenChange={open => {
            if (!open) setMapStop(null);
          }}
          title={
            mapStop === 'pickup'
              ? t('confirmation.edit.new_pickup', 'New pickup')
              : t('confirmation.edit.new_dropoff', 'New dropoff')
          }
          clientId={readString(payload, 'clientId')}
          value={stopLocationValue(
            stopPin(mapStop),
            mapStop === 'pickup' ? pickupLabel : dropoffLabel,
          )}
          onChoose={chosen => handleStopChosen(mapStop, chosen)}
        />
      ) : null}
    </div>
  );
};

/**
 * What a load price becomes once fees and tax are on it.
 *
 * Sits under the amount it belongs to rather than under the price block:
 * with two options on screen, one total floating at the bottom reads as
 * the total of whichever price the eye landed on last.
 */
const TotalWithTax = ({ amount, currency }: { amount?: number; currency?: string }) => {
  const { t } = useTranslation('m-agent');
  if (amount == null) return null;
  return (
    <span className='flex flex-wrap items-center gap-1 text-xs text-slate-500'>
      {t('confirmation.edit.total_with_tax', 'Total amount with tax')}
      <Price
        amount={amount}
        currency={currency as Parameters<typeof Price>[0]['currency']}
        size='xs'
        className='font-semibold text-slate-700'
      />
    </span>
  );
};

/** A party named on the order — clickable when the card knows its id. */
const PartyChip = ({
  kind,
  party,
}: {
  kind: HeroEntityKind;
  party: { id?: string; name: string };
}) =>
  party.id ? (
    <EntityLink kind={kind} entityId={party.id} label={party.name} variant='inline' />
  ) : (
    <span className='rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700'>
      {party.name}
    </span>
  );

const readParty = (value: unknown): { id?: string; name: string } | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const party = value as { id?: unknown; name?: unknown };
  if (typeof party.name !== 'string' || !party.name.trim()) return undefined;
  return {
    id: typeof party.id === 'string' && party.id ? party.id : undefined,
    name: party.name.trim(),
  };
};

/** A staged `OrderUpdateAPI` destination, as it survives the payload. */
type StagedDestination = {
  type?: string;
  location?: { lat?: number; lng?: number };
  isAccurateLocation?: boolean;
  locationUrl?: string;
  savedLocationId?: string;
};

const readDestinations = (value: unknown): StagedDestination[] =>
  Array.isArray(value) ? (value as StagedDestination[]) : [];

/** Open the picker on the stop's pin, labelled with what the card shows. */
const stopLocationValue = (
  dest: StagedDestination | undefined,
  label?: { name?: string; city?: string },
): ChosenLocation | null => {
  const lat = dest?.location?.lat;
  const lng = dest?.location?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  return {
    lat,
    lng,
    name: label?.name,
    city: label?.city,
    isAccurate: dest?.isAccurateLocation,
    locationUrl: dest?.locationUrl,
    savedLocationId: dest?.savedLocationId,
  };
};

/**
 * Where one moved stop landed: the city, then the place inside it as a
 * badge — the same city-primary / place-secondary reading the
 * create-order card and the location picker use.
 */
const StopPlaceLine = ({
  label,
  city,
  place,
  fallback,
  onOpenMap,
}: {
  label: string;
  city?: string;
  place?: string;
  fallback?: string;
  /** Opens the pin on a map — omitted when there is no pin to show. */
  onOpenMap?: () => void;
}) => {
  const { t } = useTranslation('m-agent');
  // A pin that resolved to nothing is the only case where the user's own
  // text is still the best label — unless that text is the Maps URL,
  // which names no place.
  const badge =
    place ??
    (fallback
      ? containsGoogleMapsUrl(fallback)
        ? t('confirmation.edit.dropped_pin', 'Shared pin')
        : fallback
      : undefined);
  if (!city && !badge) return null;

  const stopText = (
    <>
      {city ? <span className='font-medium text-slate-800'>{city}</span> : null}
      {badge ? (
        <span className='rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-700'>{badge}</span>
      ) : null}
    </>
  );

  return (
    <span className='flex flex-wrap items-center gap-1.5 text-xs text-slate-600'>
      <span className='text-slate-500'>{label}</span>
      {onOpenMap ? (
        <button
          type='button'
          onClick={onOpenMap}
          className='flex flex-wrap items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-slate-100'>
          {stopText}
          <LocationIcon className='size-3 text-primary-600' />
        </button>
      ) : (
        stopText
      )}
    </span>
  );
};

/**
 * Bulk edit card body — counts and the aggregate price movement, never a
 * 50-row list. The skip list is the important half: it is the only place
 * the user learns that three of their twelve orders won't be touched, and
 * why.
 */
const BulkEditOrdersPayloadPreview = ({
  payload,
}: {
  payload: Record<string, unknown>;
}) => {
  const { t } = useTranslation('m-agent');
  const summary = readString(payload, 'summary');
  const clientName = readString(payload, 'clientName');
  const willApply = readNumber(payload, 'willApplyCount') ?? 0;
  const skippedCount = readNumber(payload, 'skippedCount') ?? 0;
  const totalPriceDelta = readNumber(payload, 'totalPriceDelta');
  const sampleRefs = parseStringArray(payload.sampleRefs);
  const skipped = parseSkipped(payload.skipped);
  const hasMore = payload.hasMore === true;

  return (
    <div className='space-y-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs'>
      <dl className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        {summary ? (
          <Field label={t('confirmation.edit.change', 'Change')} value={summary} />
        ) : null}
        {clientName ? (
          <Field label={t('confirmation.fields.client_name', 'Client')} value={clientName} />
        ) : null}
        <Field
          label={t('confirmation.bulk_edit.will_apply', 'Orders affected')}
          value={String(willApply)}
        />
        {totalPriceDelta != null && totalPriceDelta !== 0 ? (
          <div className='flex flex-col gap-0.5'>
            <dt className='font-medium text-slate-500'>
              {t('confirmation.bulk_edit.total_delta', 'Total price change')}
            </dt>
            <dd className='text-sm'>
              <Price
                amount={totalPriceDelta}
                className={cn(
                  'font-semibold',
                  totalPriceDelta > 0 ? 'text-emerald-700' : 'text-red-700',
                )}
              />
            </dd>
          </div>
        ) : null}
      </dl>

      {sampleRefs.length > 0 ? (
        <div className='flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2 text-sm text-slate-700'>
          {sampleRefs.map(ref => (
            <span key={ref} className='rounded-md bg-white px-1.5 py-0.5 text-xs'>
              {ref}
            </span>
          ))}
          {hasMore || willApply > sampleRefs.length ? (
            <span className='text-xs text-slate-500'>
              {t('confirmation.bulk_edit.and_more', '+{{count}} more', {
                count: willApply - sampleRefs.length,
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      {skippedCount > 0 ? (
        <div className='flex flex-col gap-1 border-t border-slate-100 pt-2'>
          <span className='font-medium text-slate-500'>
            {t('confirmation.bulk_edit.skipped', 'Skipped ({{count}})', {
              count: skippedCount,
            })}
          </span>
          <ul className='space-y-0.5'>
            {skipped.slice(0, 5).map((s, i) => (
              <li key={i} className='text-sm text-slate-700'>
                {s.orderRef ? <span className='font-medium'>{s.orderRef}</span> : null}
                {s.orderRef && s.reason ? <span className='text-slate-400'> — </span> : null}
                {s.reason ? <span className='text-slate-600'>{s.reason}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Fallback body for actions without a bespoke editor.
 *
 * Three things it refuses to do, because a generic renderer is
 * exactly where they used to leak:
 *
 * - **Print an id.** A `*Id` key becomes the entity's name as a chip
 *   that opens its drawer (see {@link ENTITY_REF_KEYS}); ids with no
 *   known entity kind are dropped entirely.
 * - **Print a coordinate pair.** Handled by the bespoke bodies; here
 *   a `location` object is summarised as "Pinned on the map".
 * - **Show base info as dead text.** Strings, numbers, and booleans
 *   are inputs / switches wired to the staged payload.
 */
const GenericPayloadPreview = ({
  confirmationId,
  payload,
}: {
  confirmationId: string;
  payload: Record<string, unknown>;
}) => {
  const { t } = useTranslation('m-agent');
  const updatePayload = useAgentChatStore(s => s.updatePendingConfirmationPayload);
  const entries = Object.entries(payload).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return null;

  return (
    <div className='grid grid-cols-1 gap-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-xs sm:grid-cols-2'>
      {entries.map(([key, value]) => {
        const label = t(`confirmation.fields.${key}`, humanise(key));
        const ref = ENTITY_REF_KEYS[key];

        if (ref) {
          const displayName = ref.nameKeys
            .map(nameKey => readString(payload, nameKey))
            .find(Boolean);
          return (
            <ClientOrEntityRef
              key={key}
              label={label}
              kind={ref.kind}
              entityId={normalizeEntityId(value)}
              displayName={displayName}
              kindLabel={t(`confirmation.entity_kind.${ref.kind}`, humanise(ref.kind))}
            />
          );
        }

        // Any other identifier is noise: the user can't act on it and
        // it isn't a name. Same for the display-only labels the
        // bespoke bodies render themselves.
        if (isSuppressedKey(key)) return null;

        if (typeof value === 'boolean') {
          return (
            <CardSwitchField
              key={key}
              label={label}
              value={value}
              onChange={next => updatePayload(confirmationId, { [key]: next })}
            />
          );
        }

        if (typeof value === 'number') {
          return (
            <CardNumberField
              key={key}
              label={label}
              value={value}
              onChange={next => updatePayload(confirmationId, { [key]: next })}
            />
          );
        }

        if (typeof value === 'string') {
          return (
            <CardTextField
              key={key}
              label={label}
              value={value}
              onChange={next => updatePayload(confirmationId, { [key]: next })}
            />
          );
        }

        return <Field key={key} label={label} value={formatValue(value)} />;
      })}
    </div>
  );
};

/** Entity kinds one hook call apart, so the switch stays a component. */
const ClientOrEntityRef = ({
  label,
  kind,
  entityId,
  displayName,
  kindLabel,
}: {
  label: string;
  kind: HeroEntityKind;
  entityId: string;
  displayName?: string;
  kindLabel: string;
}) =>
  kind === 'client' ? (
    <ClientRefField label={label} clientId={entityId} fallbackName={displayName} />
  ) : (
    <EntityRefField
      label={label}
      kind={kind}
      entityId={entityId}
      fallbackLabel={displayName ?? kindLabel}
    />
  );

/**
 * Payload keys that reference another record, and where that record's
 * display name is staged. Add a row here when a new action stages a
 * new kind of reference — the id itself must never render.
 */
const ENTITY_REF_KEYS: Record<string, { kind: HeroEntityKind; nameKeys: string[] }> = {
  clientId: { kind: 'client', nameKeys: ['clientName'] },
  orderId: { kind: 'order', nameKeys: ['orderRef'] },
  driverId: { kind: 'driver', nameKeys: ['driverName'] },
  vehicleId: { kind: 'vehicle', nameKeys: ['vehiclePlate', 'plate'] },
  savedLocationId: { kind: 'saved_location', nameKeys: ['savedLocationName'] },
  contactId: { kind: 'contact', nameKeys: ['contactName'] },
  invoiceId: { kind: 'invoice', nameKeys: ['invoiceRef'] },
};

/** Display-only or non-actionable keys the generic body never shows. */
const isSuppressedKey = (key: string): boolean =>
  key === '_id' ||
  key === 'locationLabel' ||
  key === 'locationAddress' ||
  /(^|[a-z])Id$/.test(key) ||
  key.endsWith('_id');

const RolesField = ({ label, roles }: { label: string; roles: PartyRole[] }) => (
  <div className='flex flex-col gap-0.5'>
    <span className='font-medium text-slate-500'>{label}</span>
    <span className='m-0 min-w-0'>
      <ClientRolesBadge roles={roles} max={4} />
    </span>
  </div>
);

const parseRoles = (value: unknown): PartyRole[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((r): r is PartyRole => typeof r === 'string');
};

const readNumber = (payload: Record<string, unknown>, key: string): number | undefined => {
  const v = payload[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
};

const parseStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && !!v.trim()) : [];

const parseEditFees = (value: unknown): { label: string; amount: number }[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap(raw => {
    if (typeof raw !== 'object' || raw === null) return [];
    const fee = raw as { name?: unknown; type?: unknown; amount?: unknown };
    const amount = typeof fee.amount === 'number' ? fee.amount : undefined;
    if (amount == null) return [];
    const label =
      (typeof fee.name === 'string' && fee.name.trim()) ||
      (typeof fee.type === 'string' && humanise(fee.type)) ||
      '';
    return [{ label, amount }];
  });
};

const parseSkipped = (value: unknown): { orderRef?: string; reason?: string }[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap(raw => {
    if (typeof raw !== 'object' || raw === null) return [];
    const s = raw as { orderRef?: unknown; reason?: unknown };
    return [
      {
        orderRef: typeof s.orderRef === 'string' ? s.orderRef : undefined,
        reason: typeof s.reason === 'string' ? s.reason : undefined,
      },
    ];
  });
};

/**
 * The confirmation payload is `Record<string, unknown>` by the time it
 * reaches the card (it round-trips through Mongo), so the typed
 * {@link OrderLocationChangePreview} has to be re-narrowed here. Only the
 * fields the card renders are picked.
 */
const parseLocationChangePreview = (
  value: unknown,
): Pick<
  OrderLocationChangePreview,
  | 'crossCity'
  | 'distanceKm'
  | 'previousPrice'
  | 'previousRouteLabel'
  | 'newRouteLabel'
  | 'resolvedPrice'
  | 'hasRateOnNewRoute'
  | 'changes'
> | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const p = value as Record<string, unknown>;
  if (typeof p.previousPrice !== 'number') return undefined;
  return {
    changes: parsePreviewChanges(p.changes),
    crossCity: p.crossCity === true,
    distanceKm: typeof p.distanceKm === 'number' ? p.distanceKm : undefined,
    previousPrice: p.previousPrice,
    previousRouteLabel: typeof p.previousRouteLabel === 'string' ? p.previousRouteLabel : '',
    newRouteLabel: typeof p.newRouteLabel === 'string' ? p.newRouteLabel : '',
    resolvedPrice: typeof p.resolvedPrice === 'number' ? p.resolvedPrice : undefined,
    hasRateOnNewRoute: p.hasRateOnNewRoute === true,
  };
};

/** Per-stop before/after cities, the only part of `changes` the card reads. */
const parsePreviewChanges = (
  value: unknown,
): OrderLocationChangePreview['changes'] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap(raw => {
    if (typeof raw !== 'object' || raw === null) return [];
    const c = raw as Record<string, unknown>;
    if (c.type !== 'pickup' && c.type !== 'dropoff') return [];
    return [
      {
        destinationId: typeof c.destinationId === 'string' ? c.destinationId : '',
        type: c.type,
        previousCity: typeof c.previousCity === 'string' ? c.previousCity : undefined,
        newCity: typeof c.newCity === 'string' ? c.newCity : undefined,
        distanceKm: typeof c.distanceKm === 'number' ? c.distanceKm : 0,
      },
    ];
  });
};

/**
 * The staged preview as the shared price modal wants it — whole, not the
 * card's narrowed read.
 *
 * It round-trips through Mongo as a plain object, so the only checks
 * worth making are the ones the modal would crash without: a currency to
 * render and a fee list to run the Amount math over.
 */
const readLocationChangePreview = (
  value: unknown,
): OrderLocationChangePreview | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const p = value as Record<string, unknown>;
  if (typeof p.previousPrice !== 'number' || typeof p.currency !== 'string') return undefined;
  return {
    ...(p as unknown as OrderLocationChangePreview),
    fees: Array.isArray(p.fees) ? (p.fees as OrderLocationChangePreview['fees']) : [],
  };
};

const asStopLabel = (city?: string): { city: string } | undefined =>
  city?.trim() ? { city: city.trim() } : undefined;

/**
 * A staged pickup window, as either shape the tools produce.
 *
 * A bare timestamp is a start with no end — the create path gives it a
 * two-hour window, and rendering the same assumption here keeps the card
 * from saying "9:00 AM – 9:00 AM".
 */
const parseStagedDateRange = (value: unknown): { from: string; to: string } | undefined => {
  const raw =
    typeof value === 'string'
      ? { from: value }
      : typeof value === 'object' && value !== null
        ? (value as { from?: unknown; to?: unknown })
        : undefined;
  if (typeof raw?.from !== 'string' || !raw.from.trim()) return undefined;

  const from = new Date(raw.from);
  if (Number.isNaN(from.getTime())) return undefined;
  const to =
    typeof raw.to === 'string' && raw.to.trim()
      ? raw.to
      : new Date(from.getTime() + 2 * 60 * 60 * 1000).toISOString();
  return { from: raw.from, to };
};

/** The resolved name/city the tool attached to a moved stop. */
const parseStopLabel = (value: unknown): { name?: string; city?: string } | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const l = value as Record<string, unknown>;
  const name = typeof l.name === 'string' && l.name.trim() ? l.name.trim() : undefined;
  const city = typeof l.city === 'string' && l.city.trim() ? l.city.trim() : undefined;
  return name || city ? { name, city } : undefined;
};

/**
 * Last-resort renderer for a value with no better treatment. Objects
 * are summarised by their keys rather than JSON-dumped — a raw
 * `{"lat":24.15,"lng":47.32}` on screen is a bug, not a fallback.
 */
const formatValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(v => formatValue(v)).join(', ');
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .map(humanise)
      .join(', ');
  }
  return String(value);
};
